import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  INSTRUMENTS, scoreAssessment, saveAssessment,
  onIndividualAssessments, getTrendData, getInstrumentList,
} from "../services/assessmentService";
import {
  Plus, X, AlertTriangle, Check, ChevronRight, ChevronLeft,
  Activity, Clock, BarChart3,
} from "lucide-react";

const S = {
  card: { background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", borderRadius: 14, padding: 16, marginBottom: 12 },
  btn: { padding: "8px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "none" },
  label: { fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4, display: "block" },
};

// ─── Mini Trend Chart (SVG) ──────────────────────────────────────

function TrendChart({ data, maxScore }) {
  if (!data || data.length < 2) return null;
  const W = 200, H = 50, pad = 4;
  const max = maxScore || Math.max(...data.map(d => d.score), 1);
  const pts = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (W - pad * 2),
    y: pad + (1 - d.score / max) * (H - pad * 2),
  }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      {/* Grid line at midpoint */}
      <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke="rgba(99,179,237,0.06)" strokeWidth={1} />
      {/* Line */}
      <path d={line} fill="none" stroke="#4fd1c5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {/* Points */}
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill={i === pts.length - 1 ? "#63b3ed" : "#4fd1c5"} />
      ))}
    </svg>
  );
}

// ─── Assessment Administration ───────────────────────────────────

function AdministerAssessment({ instrumentKey, individual, onComplete, onCancel }) {
  const { profile } = useAuth();
  const inst = INSTRUMENTS[instrumentKey];
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(new Array(inst.questions.length).fill(null));
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const totalQuestions = inst.questions.length;
  const answered = answers.filter(a => a !== null).length;
  const allAnswered = answered === totalQuestions;

  const getOptions = (qIndex) => {
    if (inst.questionOptions) return inst.questionOptions[qIndex];
    return inst.options;
  };

  const handleAnswer = (value) => {
    const updated = [...answers];
    updated[currentQ] = value;
    setAnswers(updated);
    // Auto-advance after short delay
    if (currentQ < totalQuestions - 1) {
      setTimeout(() => setCurrentQ(currentQ + 1), 200);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const saved = await saveAssessment({
        individualId: individual.id,
        instrument: instrumentKey,
        answers,
        administeredBy: profile?.displayName || "Unknown",
      });
      setResult(saved.scoring || scoreAssessment(instrumentKey, answers));
    } catch (e) {
      console.error("Failed to save assessment:", e);
    }
    setSaving(false);
  };

  // Results view
  if (result) {
    return (
      <div style={S.card}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1 }}>
            {inst.fullName} — Results
          </div>
          <div style={{
            fontSize: 48, fontWeight: 900, fontFamily: "monospace", color: result.color, marginTop: 8,
          }}>
            {result.totalScore}{inst.maxScore !== null && <span style={{ fontSize: 18, color: "#4a5568" }}>/{inst.maxScore}</span>}
          </div>
          <div style={{
            display: "inline-block", padding: "4px 16px", borderRadius: 20, marginTop: 8,
            background: `${result.color}15`, color: result.color,
            fontSize: 14, fontWeight: 800,
          }}>
            {result.severity}
          </div>
        </div>

        {/* Recommended action */}
        <div style={{
          padding: 12, borderRadius: 10, marginBottom: 12,
          background: "rgba(99,179,237,0.04)", border: "1px solid rgba(99,179,237,0.1)",
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#63b3ed", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Recommended Action</div>
          <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.5 }}>{result.action}</div>
        </div>

        {/* Critical flag */}
        {result.criticalFlag && (
          <div style={{
            padding: 12, borderRadius: 10, marginBottom: 12,
            background: "rgba(252,129,129,0.06)", border: "1px solid rgba(252,129,129,0.15)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <AlertTriangle size={16} style={{ color: "#fc8181" }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>Critical Safety Item Flagged</div>
              <div style={{ fontSize: 11, color: "#fc8181", opacity: 0.8, marginTop: 2 }}>
                {inst.isCritical
                  ? "Suicide risk identified — follow safety protocol immediately"
                  : "Item 9 (self-harm thoughts) endorsed — assess safety and follow protocol"}
              </div>
            </div>
          </div>
        )}

        <button onClick={() => onComplete()} style={{
          ...S.btn, width: "100%", padding: 12,
          background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white",
        }}>Done</button>
      </div>
    );
  }

  // Question view
  return (
    <div>
      {/* Header */}
      <div style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>{inst.name}</div>
          <div style={{ fontSize: 10, color: "#718096" }}>{inst.fullName}</div>
        </div>
        <button onClick={onCancel} style={{ ...S.btn, background: "rgba(252,129,129,0.08)", color: "#fc8181", fontSize: 10 }}>
          <X size={12} /> Cancel
        </button>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: "#718096" }}>Question {currentQ + 1} of {totalQuestions}</span>
          <span style={{ fontSize: 10, color: "#4fd1c5", fontWeight: 700 }}>{answered}/{totalQuestions} answered</span>
        </div>
        <div style={{ width: "100%", height: 4, borderRadius: 2, background: "rgba(99,179,237,0.06)" }}>
          <div style={{
            width: `${(answered / totalQuestions) * 100}%`, height: "100%", borderRadius: 2,
            background: "#4fd1c5", transition: "width 0.3s ease",
          }} />
        </div>
      </div>

      {/* Question */}
      <div style={S.card}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
          Question {currentQ + 1}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", lineHeight: 1.5, marginBottom: 16 }}>
          {inst.questions[currentQ]}
        </div>

        {/* Answer options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {getOptions(currentQ).map(opt => (
            <button key={opt.value} onClick={() => handleAnswer(opt.value)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 16px", borderRadius: 10, textAlign: "left",
              background: answers[currentQ] === opt.value ? "rgba(79,209,197,0.12)" : "rgba(15,23,42,0.5)",
              color: answers[currentQ] === opt.value ? "#4fd1c5" : "#a0aec0",
              border: `1px solid ${answers[currentQ] === opt.value ? "rgba(79,209,197,0.3)" : "rgba(99,179,237,0.06)"}`,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>
              <span>{opt.label}</span>
              <span style={{ fontFamily: "monospace", fontSize: 11, color: "#4a5568" }}>{opt.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
          style={{
            ...S.btn, flex: 1, padding: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
            background: "rgba(99,179,237,0.06)", color: currentQ === 0 ? "#2d3748" : "#63b3ed",
          }}>
          <ChevronLeft size={14} /> Previous
        </button>

        {currentQ < totalQuestions - 1 ? (
          <button onClick={() => setCurrentQ(currentQ + 1)}
            style={{
              ...S.btn, flex: 1, padding: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              background: "rgba(99,179,237,0.06)", color: "#63b3ed",
            }}>
            Next <ChevronRight size={14} />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={!allAnswered || saving}
            style={{
              ...S.btn, flex: 1, padding: 10,
              background: allAnswered ? "linear-gradient(135deg, #38a169, #2f855a)" : "rgba(104,211,145,0.1)",
              color: allAnswered ? "white" : "#4a5568",
              opacity: saving ? 0.5 : 1,
            }}>
            {saving ? "Scoring..." : allAnswered ? "Submit & Score" : `${totalQuestions - answered} remaining`}
          </button>
        )}
      </div>

      {/* Question dots */}
      <div style={{ display: "flex", gap: 3, justifyContent: "center", marginTop: 12, flexWrap: "wrap" }}>
        {answers.map((a, i) => (
          <button key={i} onClick={() => setCurrentQ(i)} style={{
            width: 10, height: 10, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0,
            background: i === currentQ ? "#63b3ed" : a !== null ? "#4fd1c5" : "rgba(99,179,237,0.1)",
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Tab Component ──────────────────────────────────────────

export default function AssessmentTab({ individual }) {
  const { profile } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [administering, setAdministering] = useState(null); // instrument key
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!individual?.id) return;
    return onIndividualAssessments(individual.id, setAssessments);
  }, [individual?.id]);

  const instruments = getInstrumentList();

  // Group history by instrument
  const grouped = useMemo(() => {
    const map = {};
    assessments.forEach(a => {
      if (!map[a.instrument]) map[a.instrument] = [];
      map[a.instrument].push(a);
    });
    return map;
  }, [assessments]);

  // Most recent score per instrument
  const latest = useMemo(() => {
    const map = {};
    Object.keys(grouped).forEach(key => {
      map[key] = grouped[key][0]; // Already sorted desc
    });
    return map;
  }, [grouped]);

  if (!individual) return null;

  // If administering, show the assessment
  if (administering) {
    return (
      <AdministerAssessment
        instrumentKey={administering}
        individual={individual}
        onComplete={() => setAdministering(null)}
        onCancel={() => setAdministering(null)}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Assessments</div>
          <div style={{ fontSize: 10, color: "#718096" }}>{assessments.length} completed</div>
        </div>
        <button onClick={() => setShowPicker(!showPicker)} style={{
          ...S.btn,
          background: showPicker ? "rgba(252,129,129,0.1)" : "rgba(183,148,244,0.1)",
          color: showPicker ? "#fc8181" : "#b794f4",
        }}>
          {showPicker ? <><X size={12} /> Cancel</> : <><Plus size={12} /> New Assessment</>}
        </button>
      </div>

      {/* Instrument Picker */}
      {showPicker && (
        <div style={{ ...S.card, background: "rgba(183,148,244,0.03)", border: "1px solid rgba(183,148,244,0.12)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 10 }}>Select Assessment</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {instruments.map(inst => (
              <button key={inst.key} onClick={() => { setShowPicker(false); setAdministering(inst.key); }}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px 14px", borderRadius: 10, textAlign: "left",
                  background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.06)",
                  color: "#e2e8f0", cursor: "pointer",
                }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{inst.name}</div>
                  <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                    {inst.fullName} · {inst.questionCount} questions
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                    background: "rgba(183,148,244,0.08)", color: "#b794f4",
                  }}>{inst.category}</span>
                  <ChevronRight size={14} style={{ color: "#4a5568" }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Latest Scores Summary */}
      {Object.keys(latest).length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
          {Object.entries(latest).map(([key, a]) => (
            <div key={key} style={{ ...S.card, textAlign: "center", padding: 10, marginBottom: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{key}</div>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "monospace", color: a.severity === "Minimal" || a.severity === "Low Risk" || a.severity === "Below Threshold" || a.severity === "No Risk Identified" ? "#68d391" : a.severity === "Mild" || a.severity === "Hazardous Use" ? "#ecc94b" : "#fc8181", marginTop: 2 }}>
                {a.totalScore}
              </div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#718096", marginTop: 2 }}>{a.severity}</div>
            </div>
          ))}
        </div>
      )}

      {/* History by instrument */}
      {assessments.length === 0 && !showPicker ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <Activity size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>No assessments completed</div>
          <div style={{ fontSize: 11, color: "#2d3748", marginTop: 4 }}>Tap "New Assessment" to administer a screening tool.</div>
        </div>
      ) : (
        Object.entries(grouped).map(([key, items]) => {
          const inst = INSTRUMENTS[key];
          const trend = getTrendData(items, key);
          return (
            <div key={key} style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>{key}</span>
                  <span style={{ fontSize: 10, color: "#718096", marginLeft: 8 }}>{inst?.fullName}</span>
                </div>
                <button onClick={() => setAdministering(key)} style={{
                  ...S.btn, fontSize: 10, padding: "4px 10px",
                  background: "rgba(183,148,244,0.08)", color: "#b794f4",
                }}>Re-administer</button>
              </div>

              {/* Trend chart */}
              {trend.length >= 2 && (
                <div style={{ marginBottom: 10 }}>
                  <TrendChart data={trend} maxScore={inst?.maxScore} />
                </div>
              )}

              {/* History rows */}
              {items.map((a, i) => (
                <div key={a.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 0",
                  borderBottom: i < items.length - 1 ? "1px solid rgba(99,179,237,0.04)" : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20, fontWeight: 900, fontFamily: "monospace", color: "#e2e8f0", minWidth: 32 }}>
                      {a.totalScore}
                    </span>
                    <div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                        background: a.severity === "Minimal" || a.severity === "Low Risk" || a.severity === "Below Threshold" || a.severity === "No Risk Identified" ? "rgba(104,211,145,0.1)" : a.severity === "Mild" || a.severity === "Hazardous Use" ? "rgba(236,201,75,0.1)" : "rgba(252,129,129,0.1)",
                        color: a.severity === "Minimal" || a.severity === "Low Risk" || a.severity === "Below Threshold" || a.severity === "No Risk Identified" ? "#68d391" : a.severity === "Mild" || a.severity === "Hazardous Use" ? "#ecc94b" : "#fc8181",
                      }}>{a.severity}</span>
                      {a.criticalFlag && <AlertTriangle size={10} style={{ color: "#fc8181", marginLeft: 4 }} />}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "#718096" }}>{a.administeredDate}</div>
                    <div style={{ fontSize: 9, color: "#4a5568" }}>By: {a.administeredBy}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}